import { type BoardTask, type BoardTaskStatus } from '../../../../../common/types/task';
import { BoardTasksState } from '../tasksSlice';

type Comparator = (a: BoardTask, b: BoardTask) => number;

const priorityRank: Record<string, number> = {
  LOW: 0,
  MEDIUM: 1,
  LOW_GOLD: 2,
  MEDIUM_GOLD: 3,
  CRITICAL: 4,
  CRITICAL_GOLD: 5,
};

const getPriorityRank = (task: BoardTask) =>
  priorityRank[task.isGold ? `${task.priority}_GOLD` : task.priority];

const notStartedComparator: Comparator = (a, b) => {
  const p = getPriorityRank(b) - getPriorityRank(a);
  if (p !== 0) return p;

  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
};

const inProgressComparator: Comparator = (a, b) => {
  return new Date(a.startedAt || 0).getTime() - new Date(b.startedAt || 0).getTime();
};

const waitingComparator: Comparator = (a, b) => {
  const p = getPriorityRank(b) - getPriorityRank(a);
  if (p !== 0) return p;

  return new Date(a.startedAt || 0).getTime() - new Date(b.startedAt || 0).getTime();
};

const doneComparator: Comparator = (a, b) => {
  return new Date(b.completedAt || 0).getTime() - new Date(a.completedAt || 0).getTime();
};

const archivedComparator: Comparator = (a, b) => {
  return new Date(b.archivedAt || 0).getTime() - new Date(a.archivedAt || 0).getTime();
};

const comparators: Record<BoardTaskStatus, Comparator> = {
  NOT_STARTED: notStartedComparator,
  IN_PROGRESS: inProgressComparator,
  WAITING: waitingComparator,
  DONE: doneComparator,
  ARCHIVED: archivedComparator,
};

// Вставка задачи в подходящую по статусу колонку, с вычислением подходящего места
export const insertSorted = (state: BoardTasksState, task: BoardTask) => {
  const status = task.status;
  const byId = state.byId;

  const ids = state.idsByStatus[status];
  const isFullyLoaded = state.isFullyLoadedByStatus[status];
  const comparator = comparators[status];

  let left = 0;
  let right = ids.length;

  // Вставка в упорядоченный массив через бинарный поиск подходящего места
  while (left < right) {
    const mid = (left + right) >> 1;
    const midTask = byId[ids[mid]];

    if (comparator(task, midTask) < 0) {
      right = mid;
    } else {
      left = mid + 1;
    }
  }

  const isInsertToEnd = left === ids.length;

  // Не вставляем в конец списка, если есть незагруженные задачи, т.к. возможно, среди незагруженных задач есть те,
  // что должны находиться перед вставляемой задачей (вставляемая задача загрузится на следующей странице)
  if (isInsertToEnd && !isFullyLoaded) return;

  ids.splice(left, 0, task.id);
};
